import { useEffect, useRef, useState } from "react";
import { ListFilter, ChevronDown, Pin, Sparkles, Save, X } from "lucide-react";
import { Badge, Button, Input, Modal, ModalFooter, useToast } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import type { ContactList } from "@/lib/types";
import { SmartFilterBuilder, type FilterSpec } from "./SmartFilterBuilder";
import { cn } from "@/lib/utils";

/**
 * The "smart list" picker: a single dropdown that lets users pick a saved
 * smart list OR build a one-off filter inline. Used in contact lists, the
 * inbox, the calendar, tasks, pipelines — anywhere a filter on contacts
 * makes sense.
 *
 * Props:
 *  listId:  currently selected saved list id (or null for "All / ad-hoc")
 *  filter:  the active ad-hoc filter spec (overrides listId if it has rules)
 *  onChange(listId, filter): called whenever the user picks a list or edits the filter
 */
export function SmartListPicker({
  listId, filter, onChange,
}: {
  listId: string | null;
  filter: FilterSpec;
  onChange: (listId: string | null, filter: FilterSpec) => void;
}) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [lists, setLists] = useState<ContactList[]>([]);
  const [activeList, setActiveList] = useState<ContactList | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Load lists once.
  const reload = async () => {
    try {
      const d = await api.get<{ lists: ContactList[] }>("/lists");
      setLists(d.lists);
      if (listId) setActiveList(d.lists.find((l) => l.id === listId) ?? null);
    } catch { /* ignore */ }
  };
  useEffect(() => { void reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => {
    setActiveList(listId ? lists.find((l) => l.id === listId) ?? null : null);
  }, [listId, lists]);

  // Click-outside.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const adhocActive = (filter.rules?.length ?? 0) > 0 || (filter.groups?.length ?? 0) > 0;
  const totalConditions = countAllConditions(filter);
  const label = adhocActive ? "Custom filter" : (activeList?.name ?? "All contacts");
  const labelColor = activeList?.color ?? (adhocActive ? "indigo" : "zinc");

  const pickList = (l: ContactList | null) => {
    setActiveList(l);
    setOpen(false);
    onChange(l?.id ?? null, { match: "all", rules: [] });
  };

  const editAdhoc = () => {
    setOpen(false);
    setEditorOpen(true);
  };

  const onAdhocChange = (next: FilterSpec) => {
    onChange(null, next);
  };

  const saveAsList = () => {
    setEditorOpen(false);
    setSaveOpen(true);
  };

  // Single click resets to "All contacts" — same shape as a list-id reset.
  const clearActive = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null, { match: "all", rules: [] });
    setActiveList(null);
    setOpen(false);
  };
  const isActive = adhocActive || !!activeList;

  return (
    <div className="relative inline-block" ref={ref}>
      <div
        className={cn(
          "inline-flex items-center gap-1 rounded-lg border bg-zinc-900/50 h-9 text-sm transition-colors",
          isActive ? "border-indigo-500/40 text-zinc-100" : "border-zinc-800 text-zinc-300",
          "hover:bg-zinc-900",
        )}
      >
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex h-full items-center gap-2 px-3"
        >
          <ListFilter className="h-3.5 w-3.5 shrink-0" />
          <Badge color={labelColor} size="xs">{label}</Badge>
          {adhocActive && <span className="text-[10px] text-zinc-500">{totalConditions} {totalConditions === 1 ? "rule" : "rules"}</span>}
          <ChevronDown className={cn("h-3.5 w-3.5 shrink-0 transition-transform", open && "rotate-180")} />
        </button>
        {isActive && (
          <button
            type="button"
            onClick={clearActive}
            title="Clear filter — show all contacts"
            aria-label="Clear filter"
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-800 hover:text-rose-300 mr-1"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute left-0 top-full mt-1 w-72 rounded-xl border border-zinc-800 bg-zinc-950 shadow-xl shadow-shade/40 animate-fade-in z-30">
          <div className="border-b border-zinc-800 p-1">
            <button
              type="button"
              onClick={() => pickList(null)}
              className={cn(
                "flex w-full items-center justify-between rounded-lg px-2.5 py-2 text-sm hover:bg-zinc-900",
                !activeList && !adhocActive && "bg-zinc-900"
              )}
            >
              <span className="text-zinc-100">All contacts</span>
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto p-1">
            {lists.length === 0 && <p className="px-2 py-3 text-xs text-zinc-500">No saved lists yet.</p>}
            {lists.map((l) => (
              <button
                key={l.id}
                type="button"
                onClick={() => pickList(l)}
                className={cn(
                  "flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-1.5 text-sm hover:bg-zinc-900",
                  activeList?.id === l.id && "bg-zinc-900"
                )}
              >
                <span className="flex items-center gap-1.5 min-w-0">
                  <Badge color={l.color} size="xs">{l.name}</Badge>
                  {l.pinned && <Pin className="h-3 w-3 text-amber-400 shrink-0" />}
                </span>
                <span className="text-[10px] text-zinc-500 tabular-nums">{l.count}</span>
              </button>
            ))}
          </div>
          <div className="border-t border-zinc-800 p-1">
            <button
              type="button"
              onClick={editAdhoc}
              className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-sm hover:bg-zinc-900"
            >
              <Sparkles className="h-3.5 w-3.5 text-indigo-400" />
              <span className="text-zinc-100">{adhocActive ? "Edit filter" : "Build custom filter…"}</span>
            </button>
          </div>
        </div>
      )}

      {/* Inline filter editor */}
      <Modal open={editorOpen} onClose={() => setEditorOpen(false)} title="Filter contacts" size="lg">
        <SmartFilterBuilder filter={filter} onChange={onAdhocChange} />
        <ModalFooter>
          {adhocActive && (
            <Button variant="ghost" onClick={() => { onAdhocChange({ match: "all", rules: [] }); setEditorOpen(false); }}>
              Clear
            </Button>
          )}
          <div className="flex-1" />
          <Button variant="outline" onClick={saveAsList} leftIcon={<Save className="h-3.5 w-3.5" />} disabled={!adhocActive}>
            Save as smart list
          </Button>
          <Button onClick={() => setEditorOpen(false)}>Done</Button>
        </ModalFooter>
      </Modal>

      <SaveListDialog open={saveOpen} filter={filter} onClose={() => setSaveOpen(false)} onSaved={(l) => {
        setSaveOpen(false);
        toast.success(`Saved "${l.name}"`);
        reload();
        pickList(l);
      }} />
    </div>
  );
}

function countAllConditions(spec: FilterSpec): number {
  let n = spec.rules?.length ?? 0;
  for (const g of spec.groups ?? []) n += countAllConditions(g);
  return n;
}

function SaveListDialog({ open, filter, onClose, onSaved }: {
  open: boolean;
  filter: FilterSpec;
  onClose: () => void;
  onSaved: (l: ContactList) => void;
}) {
  const toast = useToast();
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setName(""); }, [open]);
  const submit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const res = await api.post<{ list: ContactList }>("/lists", { name: name.trim(), filter });
      onSaved(res.list);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to save");
    } finally { setSaving(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Save as smart list" size="sm">
      <div>
        <label className="block text-xs font-medium text-zinc-300 mb-1.5">List name</label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Hot leads in California" autoFocus />
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} loading={saving} disabled={!name.trim()}>Save</Button>
      </ModalFooter>
    </Modal>
  );
}
